import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";

export type Cheque = {
  id: string;
  user_id: string;
  numero_cheque: string;
  metodo_pagamento?: "Pix" | "Transferência" | "Maquininha de cartão" | "Cartão de crédito" | "Cartão de débito" | "Dinheiro" | "Boleto" | "Outro";
  banco: string;
  agencia: string;
  conta: string;
  emitente: string;
  cpf_cnpj?: string;
  valor: number;
  taxa_operadora_percentual?: number;
  valor_taxa_operadora?: number;
  valor_liquido?: number;
  data_emissao: string;
  data_vencimento: string;
  data_compensacao?: string;
  status: "Pendente" | "Confirmado" | "Estornado" | "Cancelado" | "A Compensar" | "Compensado" | "Devolvido";
  observacoes?: string;
  created_at: string;
  updated_at: string;
};

export function useCheques() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: cheques = [], isLoading } = useQuery({
    queryKey: ["cheques"],
    queryFn: async () => {
      const data = await apiList<Cheque>("cheques");
      return data.sort((a, b) => String(a.data_vencimento || "").localeCompare(String(b.data_vencimento || "")));
    },
    enabled: !!user,
  });

  const createMutation = useMutation({
    mutationFn: async (data: Omit<Cheque, "id" | "user_id" | "created_at" | "updated_at">) => {
      return apiCreate<Cheque>("cheques", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cheques"] });
      toast({
        title: "Sucesso",
        description: "Pagamento cadastrado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao cadastrar pagamento: " + error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Cheque> & { id: string }) => {
      return apiUpdate<Cheque>("cheques", id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cheques"] });
      toast({
        title: "Sucesso",
        description: "Pagamento atualizado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao atualizar pagamento: " + error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiDelete("cheques", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cheques"] });
      toast({
        title: "Sucesso",
        description: "Pagamento excluído com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao excluir pagamento: " + error.message,
        variant: "destructive",
      });
    },
  });

  return {
    cheques,
    isLoading,
    createCheque: createMutation.mutateAsync,
    updateCheque: updateMutation.mutateAsync,
    deleteCheque: deleteMutation.mutateAsync,
  };
}
